import { apiSlice } from './slices/apiSlice'

import notificationReducer, {
  pushNotification,
  setAppLoading,
} from '@/store/slices/notificationSlice'
import userReducer from '@/store/slices/userSlice'
import { analytics } from '@/utils/analytics'
import type { Middleware, MiddlewareAPI } from '@reduxjs/toolkit'
import {
  configureStore,
  isFulfilled,
  isPending,
  isRejectedWithValue,
} from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/query'
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux'
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import contentfulReducer from './slices/contentfulSlice'

const userPersistConfig = {
  key: 'user',
  storage,
  whitelist: ['selectedBillingAccounts'],
}

const persistedUserReducer = persistReducer(userPersistConfig, userReducer)

export const rtkQueryErrorLogger: Middleware =
  (_api: MiddlewareAPI) => (next) => (action) => {
    if (isRejectedWithValue(action)) {
      store.dispatch(
        pushNotification({
          id: Date.now().toString(),
          message:
            'Oops! Something went wrong! Help us improve your experience by sending an error report',
          severity: 'error',
        })
      )

      // Track API failure
      const payload = action.payload as any
      const meta = action.meta as any

      analytics.trackApiFailure({
        endpoint: meta?.arg?.endpointName || 'unknown',
        method: meta?.baseQueryMeta?.request?.method || 'unknown',
        statusCode: payload?.status,
        errorMessage:
          payload?.data?.message || payload?.error || 'API request failed',
        errorCode: payload?.data?.code,
      })
    }

    return next(action)
  }

export const apiLoadingMiddleware: Middleware =
  (_api: MiddlewareAPI) => (next) => (action) => {
    if (isPending(action)) {
      store.dispatch(setAppLoading(true))
    } else if (isFulfilled(action) || isRejectedWithValue(action)) {
      store.dispatch(setAppLoading(false))

      // Track API success
      if (isFulfilled(action)) {
        const meta = action.meta as any
        const startTime = meta?.startedTimeStamp
        const endTime = Date.now()

        analytics.trackApiSuccess({
          endpoint: meta?.arg?.endpointName || 'unknown',
          method: meta?.baseQueryMeta?.request?.method || 'unknown',
          statusCode: meta?.baseQueryMeta?.response?.status || 200,
          responseTime: startTime ? endTime - startTime : undefined,
        })
      }
    }

    return next(action)
  }

const store = configureStore({
  reducer: {
    [apiSlice.reducerPath]: apiSlice.reducer,
    user: persistedUserReducer,
    notification: notificationReducer,
    contentful: contentfulReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
      .concat(apiSlice.middleware)
      .concat(apiLoadingMiddleware)
      .concat(rtkQueryErrorLogger),
})

setupListeners(store.dispatch)

export const persistor = persistStore(store)

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch

export const useAppDispatch: () => AppDispatch = useDispatch
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector

export default store
